import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { BaseDestroyableComponent } from '@shared/base-classes/base-destroyable';
import { combineLatest, Observable } from 'rxjs';
import { DentureDetailsFacade } from '@modules/denture-details/denture-details.facade';
import { IdName } from '@shared/models/id-name';
import { ShadeSystem } from '@shared/models/shade-system';
import { debounceTime, distinctUntilChanged, shareReplay, take, takeUntil, tap } from 'rxjs/operators';
import { DentureDetailsState } from '@modules/denture-details/state/denture-details.store';
import { TranslateModule } from '@ngx-translate/core';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { NgIf, AsyncPipe } from '@angular/common';
import { MatDividerModule } from '@angular/material/divider';
import { IsDisabledDirective } from '@shared/directives/is-disabled/is-disabled.directive';
import { MatLegacySlideToggleModule } from '@angular/material/legacy-slide-toggle';
import { MouldsDropdownComponent } from '../../components/moulds-dropdown/moulds-dropdown.component';
import { StringSelectorComponent } from '@shared/components/object-selector/object-selector.component';
import { FlexModule } from '@angular/flex-layout/flex';
import { dentureOptionsArchClicked } from '@core/services/logger/bi-actions';
import { BiLoggerService } from '@core/services/logger/bi-logger.service';
import { observeJawSelection } from '@shared/utils/jaw.util';
import { debounceDelay } from '@shared/models/consts';
import { SelectComponent } from '@shared/components/select/select.component';
import { Form } from '@shared/utils/type-util';
import { Maybe } from '@/types/globals';

@Component({
	selector: 'rx-denture-details',
	templateUrl: './denture-details.component.html',
	styleUrls: ['./denture-details.component.scss'],
	providers: [DentureDetailsFacade],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		FlexModule,
		ReactiveFormsModule,
		SelectComponent,
		MouldsDropdownComponent,
		MatLegacySlideToggleModule,
		IsDisabledDirective,
		MatDividerModule,
		NgIf,
		StringSelectorComponent,
		MatLegacyFormFieldModule,
		MatLegacyInputModule,
		AsyncPipe,
		TranslateModule
	]
})
export class DentureDetailsComponent extends BaseDestroyableComponent implements OnInit {
	protected isReadOnly: boolean;

	form = this.formBuilder.group<Form<DentureDetailsState>>({
		isUpperJawChecked: this.formBuilder.control<boolean>(false, { nonNullable: true }),
		isLowerJawChecked: this.formBuilder.control<boolean>(false, { nonNullable: true }),
		stage: this.formBuilder.control<Maybe<IdName>>(null),
		mould: this.formBuilder.control<Maybe<IdName>>(null),
		shadeSystem: this.formBuilder.control<Maybe<ShadeSystem>>(null),
		teethShade: this.formBuilder.control<Maybe<string>>(null),
		gingivalShade: this.formBuilder.control<Maybe<IdName>>(null),
		isDentureCopyScan: this.formBuilder.control<boolean>(false, { nonNullable: true })
	});

	stages$: Observable<IdName[]> = this.facade.stages$;
	moulds$: Observable<IdName[]> = this.facade.moulds$;
	shadeSystems: ShadeSystem[] = this.facade.shadeSystems ?? [];
	teethShades$: Observable<string[] | undefined> = this.facade.teethShades$;
	gingivalShades$: Observable<IdName[]> = this.facade.gingivalShades$;
	teethShadesHasOptions$: Observable<boolean> = this.facade.shouldTeethShadeBeSelected$;
	dentureUpperSectionIsReadonly$: Observable<boolean | undefined> = this.facade.dentureUpperSectionIsReadonly$;
	dentureLowerSectionIsReadonly$: Observable<boolean | undefined> = this.facade.dentureLowerSectionIsReadonly$;
	isReadOnly$: Observable<boolean> = this.facade.isReadonly$.pipe(
		tap(isReadOnly => (this.isReadOnly = isReadOnly)),
		shareReplay({ refCount: true, bufferSize: 1 })
	);
	shouldValidateForSend$: Observable<boolean> = this.facade.shouldValidateForSend$;

	get isUpperControl() {
		return this.form.controls.isUpperJawChecked;
	}

	get isLowerControl() {
		return this.form.controls.isLowerJawChecked;
	}

	get stageControl() {
		return this.form.controls.stage;
	}

	get mouldControl() {
		return this.form.controls.mould;
	}

	get shadeSystemControl() {
		return this.form.controls.shadeSystem;
	}

	get teethShadeControl() {
		return this.form.controls.teethShade;
	}

	get gingivalShadeControl() {
		return this.form.controls.gingivalShade;
	}

	constructor(
		protected readonly formBuilder: FormBuilder,
		protected readonly facade: DentureDetailsFacade,
		private readonly biLogger: BiLoggerService
	) {
		super();
	}

	ngOnInit(): void {
		this.subscribeToUserChanges();
		this.subscribeToUserBiEvents();
		this.loadFromStore();
		this.subscribeToRxLoaded();
		this.subscribeToProgramaticallyChangingToggles();
		this.subscribeToGetDefaultShade();
		this.subscribeToDentureCopyScanChange();
	}

	protected loadFromStore(): void {
		this.form.patchValue(this.facade.storeState, { emitEvent: false });
	}

	cleanControlWhenDisabled(isDisabled: boolean, formControl: FormControl): void {
		if (isDisabled && !this.isReadOnly) {
			formControl.patchValue(null);
		}
	}

	private subscribeToUserChanges(): void {
		this.form.valueChanges
			.pipe(
				distinctUntilChanged(),
				tap(() => {
					const result = this.facade.updateStore(this.form.getRawValue());

					if (result.isFormValuesModified) {
						this.form.patchValue(result.formValues, { emitEvent: false });
					}
				}),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	private subscribeToRxLoaded(): void {
		this.facade
			.getLoadedRx()
			.pipe(
				tap(state => this.form.patchValue(state, { emitEvent: false })),
				take(1),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	private subscribeToProgramaticallyChangingToggles(): void {
		combineLatest([this.facade.getUpperSectionToggleValueWhenToothSelected$, this.facade.checkIsToothDiagramEnabled$])
			.pipe(
				tap(([isUpperJawChecked, isToothDiagramEnabled]) => {
					if (isUpperJawChecked) {
						this.form.patchValue({ ...this.form.getRawValue(), isUpperJawChecked });
					} else if (!isUpperJawChecked && isToothDiagramEnabled) {
						this.form.patchValue({ ...this.form.getRawValue(), isUpperJawChecked });
					}
				}),
				takeUntil(this.componentAlive$)
			)
			.subscribe();

		combineLatest([this.facade.getLowerSectionToggleValueWhenToothSelected$, this.facade.checkIsToothDiagramEnabled$])
			.pipe(
				tap(([isLowerJawChecked, isToothDiagramEnabled]) => {
					if (isLowerJawChecked) {
						this.form.patchValue({ ...this.form.getRawValue(), isLowerJawChecked });
					} else if (!isLowerJawChecked && isToothDiagramEnabled) {
						this.form.patchValue({ ...this.form.getRawValue(), isLowerJawChecked });
					}
				}),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	private subscribeToGetDefaultShade(): void {
		this.facade
			.getDefaultShadeSystem()
			.pipe(
				tap(shadeSystem => {
					if (!this.shadeSystemControl.value && shadeSystem) {
						this.form.patchValue({ ...this.form.getRawValue(), shadeSystem });
					}
				}),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	private subscribeToDentureCopyScanChange(): void {
		this.facade.dentureCopyScanValue$
			.pipe(
				tap((isDentureCopyScan: boolean) => {
					const control = this.form.controls.isDentureCopyScan;

					if (control && control.value !== isDentureCopyScan) {
						control.setValue(isDentureCopyScan, { emitEvent: false });
					}
				}),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	get isTeethShadeEnabled() {
		return !!this.shadeSystemControl?.value;
	}

	private subscribeToUserBiEvents(): void {
		observeJawSelection(this.isUpperControl, this.isLowerControl)
			.pipe(debounceTime(debounceDelay), distinctUntilChanged(), takeUntil(this.componentAlive$))
			.subscribe(selectedCombination => this.biLogger.userActionEvent(dentureOptionsArchClicked(selectedCombination)));
	}
}
